import { DecisionType } from "../../common/decisions";
import { compact, identity } from "lodash";
import {
  addCompaniesToPortfolio,
  closeCompanyDialog,
  closeMoveToPortfolioDialog,
  closeProjectDialog,
  closeRemoveFromPortfolioDialog,
  companiesInPortfolio,
  ConnectSubsidiaryItemState,
  createBatchProject,
  dissolvePortfolio,
  extractUserCompaniesInPortfolio,
  filterPortfolioWithText,
  InitPortfolioCompaniesPageProps,
  isSamePortfolio,
  mapToHeader,
  moveCompanyToPortfolio,
  navigateToCompaniesTabAction,
  newProjectDialog,
  NotConnectedTabState,
  openCompanyDialog,
  openExportShareholdersDialog,
  openInviteAdminDialog,
  openMoveToPortfolioDialog,
  openRemoveFromPortfolioDialog,
  openSendAdministratorInvitationDialog,
  portfolioApi,
  PortfolioCompaniesTab,
  portfolioCreationInfo,
  PortfolioPageDispatcher,
  PortfolioPageState,
  PortfolioPageStore,
  PortfolioResponse,
  PortfolioSettingsActions,
  PreparedPageState,
  ReadyState,
  removeFromPortfolio,
  savePortfolioSettings,
  searchForCompany,
  searchSwedishBusinessRegister,
  selectCompany,
  selectCompanyFromSearchResult,
  SelectedPortfolioOption,
  selectSwedishCompany,
  sendAskForPermissionRequest,
  setDestinationPortfolio,
  subsidiariesToPortfolioCompany,
  sumFollowUpCounts,
  toggleAllCompanies,
  toggleCompanyInAddDialog,
  toggleConfirmDissolvePortfolio,
  toggleDecision,
  toggleNorwegianCompanySearch,
  toggleOffboardCompanies,
  togglePortfolioSettings,
  toggleSwedishCompanySearch,
  updateStore,
} from "../portfolioDomain";
import { Language } from "../../i18n/languages";
import { AccessType } from "../../types/enums/AccessType";
import { MeetingType } from "../../meetings/meetingsDomain";
import { CompanyState, InvitationResponse, PortfolioCompany } from "../../stores/PortfolioStore";
import { SuggestedCompany } from "../../types/CompanyInfo";
import { TFunction } from "../../i18n/tt";
import { createProjectName } from "../../dbot/projectList/projectListDomain";
import {
  DCEvent,
  navigate,
  showAlert,
  showCreatePortfolioDialog,
  showError,
} from "../../events/eventsDomain";
import {
  adminBeneficialOwners,
  adminProjects,
  adminShareholdersAltinn,
} from "../../utils/navigation";
import { StarredCompany } from "../../types/UserInfo";
import { CompanyItem, emailValidator, failure, pending, Portfolio } from "../../common/common";
import { TMessageKey } from "../../i18n/types.gen";
import { ApiService, HttpStatus, isHttpError } from "../../api";
import { BooleanFieldModel, RadioFieldModel, StringFieldModel } from "../../common/commonForms";
import { TabButtonProps } from "../../components-v2/Tabs";
import { prepAcceptedTermsState } from "../acceptedTermsDomain";
import { SelectProps } from "../../components-v2/Select";
import { InviteUserToCustomRoleModel } from "../contacts/customRoleDomain";
import { CustomRoleItem } from "../../common/customRole";

export const prepareState = ({
  store,
  dispatch,
  t,
  portfolio,
  onRemovePortfolio,
  onUpdatePortfolio,
  locale,
  setPortfolioFollowUpCount,
  setCustomRoleId,
  apiService,
}: {
  store: PortfolioPageStore;
  dispatch: PortfolioPageDispatcher;
  t: TFunction;
  portfolio: SelectedPortfolioOption;
  onRemovePortfolio: (portfolioId: string) => void;
  onUpdatePortfolio: (portfolio: Portfolio) => void;
  locale: Language;
  setPortfolioFollowUpCount?: (count: number) => void;
  setCustomRoleId?: (roleId: string) => void;
  apiService: ApiService;
}): PreparedPageState => {
  const isSystemPortfolio = portfolio.type !== "portfolio-instance";
  const portfolioState: PortfolioPageState = store.portfolioState ?? {
    type: "loading-portfolio",
    portfolio,
  };
  switch (portfolioState.type) {
    case "invalid-portfolioid":
      return { state: portfolioState };
    case "loading-portfolio":
      return { state: portfolioState };
    case "ready":
      return {
        state: portfolioState,
        acceptedTerms: prepAcceptedTermsState(
          portfolioState,
          portfolioState.portfolioResponse,
          dispatch,
        ),
        actions: {
          notConnected: notConnectedCompaniesActions(dispatch, portfolioState),
          addCompanyToPortfolioActions: addCompanyToPortfolioActions(
            dispatch,
            portfolioState,
            store,
            setPortfolioFollowUpCount,
          ),
          companySearchActions: searchActions(dispatch, portfolioState),
          newProjectActions: newProjectActions(dispatch, portfolioState, t),
          portfolioActions: portfolioInstanceActions(
            portfolioState,
            dispatch,
            store,
            isSystemPortfolio,
            setCustomRoleId,
            setPortfolioFollowUpCount,
          ),
          openPortfolioSettings: () => dispatch(togglePortfolioSettings(portfolioState, true)),
          closePortfolioSettings: () => dispatch(togglePortfolioSettings(portfolioState, false)),
          savePortfolioSettings: () =>
            dispatch(savePortfolioSettings(portfolioState, onUpdatePortfolio)),
          confirmDissolvePortfolio: () =>
            dispatch(toggleConfirmDissolvePortfolio(portfolioState, true)),
          closeConfirmDissolvePortfolio: () =>
            dispatch(toggleConfirmDissolvePortfolio(portfolioState, false)),
          dissolvePortfolio: () => dispatch(dissolvePortfolio(portfolioState, onRemovePortfolio)),
          closePendingInvitationDialog: () =>
            dispatch([updateStore(portfolioState, { openInvitationRequestId: undefined })]),
          toggleOffboardCompanies: () => dispatch(toggleOffboardCompanies(portfolioState)),
          openInviteAdministratorDialog: () =>
            dispatch(openInviteAdminDialog(locale, portfolioState.portfolioResponse.companyItems)),
          openExportShareholdersDialog: () =>
            dispatch(openExportShareholdersDialog(portfolioState.portfolioResponse, dispatch)),
          openCreateProjectDialog: (decisionType) =>
            dispatch(openCreateProjectDialog({ portfolioState, decisionType })),
          setCompaniesWithSubscriptionStatus: (status) =>
            dispatch(setCompaniesWithSubscriptionStatus(portfolioState, status)),
          openCreatePortfolioDialog: () => dispatch([showCreatePortfolioDialog]),
          getCustomRoleItems: () =>
            dispatch(
              fetchCustomRoleItems(
                portfolioState.portfolio,
                portfolioState,
                portfolioState.portfolioResponse.portfolio?.id,
              ),
            ),
          openInviteCustomRoleDialog: (customRoleItem) =>
            dispatch(
              openInviteCustomRoleDialog({
                state: portfolioState,
                dispatch,
                locale,
                apiService,
                customRoleItem,
              }),
            ),
        },
      };
  }
};

export const openInviteCustomRoleDialog = ({
  state,
  dispatch,
  locale,
  apiService,
  customRoleItem,
}: {
  state: ReadyState;
  dispatch: PortfolioPageDispatcher;
  locale: Language;
  apiService: ApiService;
  customRoleItem: CustomRoleItem;
}): DCEvent[] => {
  const creatorCompany = state.userCompaniesInPortfolio
    .filter((allCompanies) => allCompanies.accessType === AccessType.ADMIN)
    .find((adminCompany) => adminCompany.organisationNumber === customRoleItem.role.orgNo);
  return [
    {
      type: "show-dialog",
      dialog: {
        type: "invite-custom-role-user",
        model: new InviteUserToCustomRoleModel({
          customRoleItem,
          availableCompanies: state.customRoleItem.availableCompanies,
          apiService,
          locale,
          dispatch,
          isLoadingInvite: false,
          showEmailField: true,
          hasAdminInCreatorCompany: creatorCompany !== undefined,
          selectedUserCompanyItems: [],
          userName: "",
          portfolioName: state.portfolio.label,
        }),
      },
    },
  ];
};

const fetchCustomRoleItems = (
  portfolio: SelectedPortfolioOption,
  state: ReadyState,
  portfolioId?: string,
): DCEvent[] => {
  return [
    {
      type: "update-store",
      data: {
        portfolioState: {
          ...state,
          isLoadingCustomRole: true,
        },
      },
    },
    portfolioApi.getCustomRoleItems({
      portfolio,
      portfolioId,
      onSuccess: (res) => {
        return [
          {
            type: "update-store",
            data: {
              portfolioState: {
                ...state,
                isLoadingCustomRole: false,
                customRoleItem: res.data,
              },
            },
          },
        ];
      },
      onFailure: () => [
        showError({ key: "general-error" }, { autoHide: true }),
        updateStore(state, {
          ...state,
          isLoadingCustomRole: false,
          customRoleItem: {
            customRoleItems: [],
            availableCompanies: [],
          },
        }),
      ],
    }),
  ];
};

export const setCompaniesWithSubscriptionStatus = (
  state: ReadyState,
  status: ActiveSubscribedCompanies,
): DCEvent[] => {
  return [
    updateStore(state, {
      ...state,
      companySubscription: status,
    }),
  ];
};

export const openCreateProjectDialog = ({
  portfolioState,
  decisionType,
}: {
  portfolioState: ReadyState;
  decisionType: DecisionType;
}): DCEvent[] => {
  const adminCompanies = portfolioState.userCompaniesInPortfolio.filter(
    (c) => c.accessType === AccessType.ADMIN,
  );
  if (adminCompanies.length !== 1) {
    return openCreateBatchProjectDialog(portfolioState, decisionType);
  } else {
    return openDBotProjectList(portfolioState);
  }
};

export const openCreateBatchProjectDialog = (
  portfolioState: ReadyState,
  decisionType: DecisionType,
): DCEvent[] => {
  const orgNo = portfolioState.userCompaniesInPortfolio
    .filter((c) => c.accessType === AccessType.ADMIN)
    .map((f) => f.organisationNumber);
  const index = portfolioState.checkedDecisions.indexOf(decisionType);
  const checkedDecisions =
    index > -1
      ? portfolioState.checkedDecisions.filter((d) => d !== decisionType)
      : portfolioState.checkedDecisions.concat(decisionType);
  return [
    updateStore(portfolioState, {
      isNewProjectDialogOpen: true,
      selectedCompanies: portfolioState.selectedCompanies.concat(orgNo),
      checkedDecisions,
    }),
  ];
};

export const initPortfolioCompaniesPage = ({
  dispatch,
  currentUrl,
  portfolio,
  activeHeader,
  companiesTab,
  companiesMode,
  signDocumentCounts,
  store,
  setPortfolioFollowUpCount,
  force,
}: InitPortfolioCompaniesPageProps): DCEvent[] => {
  const header = mapToHeader(activeHeader);
  const activeCompaniesTab: PortfolioCompaniesTab = companiesTab ?? "all";
  const portfolioState = store.portfolioState;

  // keep state when switch tabs
  if (
    companiesTab &&
    !force &&
    portfolioState &&
    isSamePortfolio(portfolio, portfolioState.portfolio)
  ) {
    if (portfolioState.companiesTab !== activeCompaniesTab && portfolioState.type === "ready") {
      return [
        updateStore(portfolioState, {
          companiesTab: activeCompaniesTab,
          portfolioSearchModel: new StringFieldModel(),
          userCompaniesInPortfolio: extractUserCompaniesInPortfolio(
            portfolioState.portfolioResponse,
            activeCompaniesTab,
          ),
          companiesTabs: createCompaniesTabs(
            portfolioState.portfolioResponse,
            dispatch,
            extractUserCompaniesInPortfolio(portfolioState.portfolioResponse, "all"),
            activeCompaniesTab,
            portfolio,
            portfolioState.notConnected,
          ),
        }),
      ];
    }
    return [];
  }

  return [
    {
      type: "update-store",
      data: {
        portfolioState: { type: "loading-portfolio", portfolio, activeHeader: "companies" },
      },
    },
    portfolioApi.getPortfolio({
      portfolio,
      onSuccess: (res) => {
        setPortfolioFollowUpCount?.(sumFollowUpCounts(res.data));

        const allCompanies = companiesInPortfolio(res.data);
        const notConnected = mapToNotConnectedState(res.data, allCompanies, "all");
        const userCompaniesInPortfolio = extractUserCompaniesInPortfolio(
          res.data,
          activeCompaniesTab,
        );
        const alreadyInPortfolio = companiesInPortfolio(res.data);
        const totalSignDocumentCount = res.data.signDocumentCount;
        return [
          {
            type: "update-store",
            data: {
              portfolioState: {
                type: "ready",
                signDocumentCounts,
                portfolio,
                companiesTab: activeCompaniesTab,
                portfolioSearchModel: new StringFieldModel(),
                isAddCompanyDialogOpen: false,
                portfolioResponse: res.data,
                selectedCompanies: [],
                checkedDecisions: [],
                companiesMode:
                  companiesMode ?? createCompaniesModeRadioFieldModel(res.data.companyItems),
                availableCompanies: res.data.userCompanies.filter(
                  (c) => !alreadyInPortfolio.includes(c),
                ),
                userCompaniesInPortfolio,
                companiesTabs: createCompaniesTabs(
                  res.data,
                  dispatch,
                  allCompanies,
                  activeCompaniesTab,
                  portfolio,
                  notConnected,
                ),
                activeHeader: header,
                currentUrl,
                totalSignDocumentCount,
                portfolioSettingsModel: {
                  name: new StringFieldModel({ value: res.data.portfolio?.name, required: true }),
                },
                notConnected,
                portfolioCreationInfo: portfolioCreationInfo(res.data.portfolio),
                acceptTermsEmail: new StringFieldModel({
                  validator: emailValidator,
                  required: true,
                  value: res.data.user.email,
                }),
                acceptTermsCheckbox: new BooleanFieldModel({ requiredTrue: true }),
                companySubscription: "all",
                adminsCompanyCount: res.data.userCompanies.filter(
                  (allCompanies) => allCompanies.accessType === AccessType.ADMIN,
                ).length,
                isLoadingCustomRole: false,
                customRoleItem: {
                  customRoleItems: [],
                  availableCompanies: [],
                },
              },
            },
          },
        ];
      },
      onFailure: () => [
        {
          type: "update-store",
          data: { portfolioState: { type: "invalid-portfolioid", portfolio } },
        },
      ],
    }),
  ];
};

export function filteredUserCompanies(state: ReadyState) {
  const isActive = state.companySubscription === "active";
  const activeCompanies = new Set(
    state.portfolioResponse.activeSubscriptionCompanies?.map(
      (subscribed) => subscribed.organisationNumber,
    ),
  );
  const filteredCompanies =
    state.companySubscription === "all"
      ? state.userCompaniesInPortfolio
      : state.userCompaniesInPortfolio.filter((company) => {
          const isSubscribed = activeCompanies.has(company.organisationNumber);
          return isActive ? isSubscribed : !isSubscribed;
        });
  const query = state.portfolioSearchModel.inputValue.toLowerCase();
  return filteredCompanies.filter(
    (c) => c.name.toLowerCase().includes(query) || c.organisationNumber.includes(query),
  );
}

export const mapToNotConnectedState = (
  data: PortfolioResponse,
  all: PortfolioCompany[],
  roleFilter: RoleFilter = "all",
): NotConnectedTabState => ({
  ...defaultNotConnectedState,
  companies: toPortfolioCompanies(data.incompletedInvitationRequests)
    .concat(all.filter((c) => !c.isCustomer))
    .concat(subsidiariesToPortfolioCompany(data.subsidiaries, data.companyItems))
    .filter(
      (c) =>
        roleFilter === "all" ||
        (roleFilter === "chairperson" && c.isChairperson) ||
        (roleFilter === "ceo" && c.isCEO) ||
        (roleFilter === "member" && c.isBoardmember) ||
        (roleFilter === "shareholder" && c.isShareholder),
    ),
});

export type AddCompanyToPortfolioActions = {
  openAddCompanyDialog: () => void;
  closeAddCompanyDialog: () => void;
  saveCompaniesInPortfolio: () => void;
  toggleCompanyInAddDialog: (orgNo: string) => void;
  isCompanySelected: (orgNo: string) => boolean;
  toggleAllCompanies: () => void;
  deselectAllCompanies: () => void;
};

export type CompanySearchActions = {
  openNorwegianCompanySearch: () => void;
  openSwedishCompanySearch: () => void;
  closeNorwegianCompanySearch: () => void;
  closeSwedishCompanySearch: () => void;
  selectCompany: (company: SuggestedCompany) => void;
  selectSwedishCompany: (company: SuggestedCompany) => void;
  searchForCompany: (query: string) => void;
  sendAskForPermissionRequest: (autoonboard: boolean) => void;
  searchSwedishBusinessRegister: () => void;
};

export type NewProjectActions = {
  openNewProjectDialog: () => void;
  closeNewProjectDialog: () => void;
  toggleDecision: (decisionType: DecisionType) => void;
  checkedDecisions: DecisionType[];
  companiesMode: RadioFieldModel<NewProjectCompaniesMode>;
  createBatchProject: () => void;
};

export type NewProjectCompaniesMode = "portfolio" | "selected_companies";

export type ActionsForNotConnectedCompanies = {
  toggleConnectAllDialog: () => void;
  toggleCheckedSubsidiary: (subsidiary: ConnectSubsidiaryItemState) => void;
  submitConnectAll: (autoonboard: boolean) => void;
  selectAll: () => void;
  selectNone: () => void;
  filterWithRole: (role: RoleFilter) => void;
};

export type PortfolioInstanceActions = {
  onAddAdministrator: ({ companyItem, lang }: { companyItem: CompanyItem; lang: Language }) => void;
  isRenderStar: (organisationNumber: string, accessType: AccessType) => boolean;
  onClickStar: (organisationNumber: string, accessType: AccessType) => void;
  onNewMeeting: (organisationNumber: string, meetingType: MeetingType) => void;
  onSelect: (company: PortfolioCompany) => void;
  filterWithText: () => void;
  onRemoveFromPortfolio?: (company: PortfolioCompany) => void;
  closeRemoveCompanyDialog: () => void;
  removeFromPortfolio: () => void;
  onMoveToPortfolio?: (company: PortfolioCompany) => void;
  closeMoveCompanyToPortfolioDialog: () => void;
  setDestinationPortfolio: (portfolio: Portfolio) => void;
  moveCompanyToPortfolio: () => void;
  openDBotProjectList: () => void;
  openBeneficialOwnersPage: (company: PortfolioCompany) => void;
  openARPage: (company: PortfolioCompany) => void;
  copyToClipboard: (text: string) => void;
};

export type PortfolioPageActions = {
  notConnected: ActionsForNotConnectedCompanies;
  addCompanyToPortfolioActions: AddCompanyToPortfolioActions;
  companySearchActions: CompanySearchActions;
  newProjectActions: NewProjectActions;
  portfolioActions: PortfolioInstanceActions;
  closePendingInvitationDialog: () => void;
  openInviteAdministratorDialog: () => void;
  openExportShareholdersDialog: () => void;
  openCreateProjectDialog: (decisionType: DecisionType) => void;
  setCompaniesWithSubscriptionStatus: (status: ActiveSubscribedCompanies) => void;
  openCreatePortfolioDialog: () => void;
  getCustomRoleItems: () => void;
  openInviteCustomRoleDialog: (customRoleItem: CustomRoleItem) => void;
} & PortfolioSettingsActions;

export function createCompaniesModeRadioFieldModel(companies: CompanyItem[]) {
  return new RadioFieldModel<NewProjectCompaniesMode>({
    value: "portfolio",
    options: ["portfolio", "selected_companies"],
    idFn: identity,
    labelFn: (l) =>
      l === "portfolio"
        ? {
            key: "portfolio.newProject.entire-portfolio",
            params: { n: companies.length },
          }
        : { key: "portfolio.batch-project.select-companies" },
  });
}

const notConnectedCompaniesActions = (
  dispatch: PortfolioPageDispatcher,
  state: ReadyState,
): ActionsForNotConnectedCompanies => {
  return {
    toggleConnectAllDialog: () =>
      dispatch([
        updateNotConnectedTabState(state, {
          showConnectAllDialog: !state.notConnected.showConnectAllDialog,
          subsidiaries: mapToConnectState(state),
        }),
      ]),
    toggleCheckedSubsidiary: (s) => {
      const subsidiaries = state.notConnected.subsidiaries;
      subsidiaries[subsidiaries.indexOf(s)] = { ...s, checked: !s.checked };
      dispatch([updateNotConnectedTabState(state, { subsidiaries })]);
    },
    submitConnectAll: (autoonboard: boolean) => dispatch(submitConnectAll(state, autoonboard)),
    selectAll: () =>
      dispatch([
        updateNotConnectedTabState(state, {
          subsidiaries: state.notConnected.subsidiaries.map((s) => ({ ...s, checked: true })),
        }),
      ]),
    selectNone: () =>
      dispatch([
        updateNotConnectedTabState(state, {
          subsidiaries: state.notConnected.subsidiaries.map((s) => ({ ...s, checked: false })),
        }),
      ]),
    filterWithRole: (value) => {
      state.notConnected.rolesFilter.setValue(value);
      dispatch([
        updateNotConnectedTabState(state, {
          companies: mapToNotConnectedState(
            state.portfolioResponse,
            state.userCompaniesInPortfolio,
            value,
          ).companies,
        }),
      ]);
    },
  };
};
export type RoleFilter = "all" | "ceo" | "chairperson" | "member" | "shareholder";
const roleFilterLabels: { [key in RoleFilter]: TMessageKey } = {
  ["all"]: "roles.all",
  ["ceo"]: "roles.ceo",
  ["chairperson"]: "roles.chairperson",
  ["member"]: "roles.member",
  ["shareholder"]: "portfolio.SHAREHOLDER",
};

export const defaultNotConnectedState: NotConnectedTabState = {
  showConnectAllDialog: false,
  subsidiaries: [],
  companies: [],
  rolesFilter: new RadioFieldModel<RoleFilter>({
    value: "all",
    options: ["all", "ceo", "chairperson", "member", "shareholder"],
    idFn: identity,
    labelFn: (l) => ({ key: roleFilterLabels[l] }),
  }),
};

const mapToConnectState = (state: ReadyState): ConnectSubsidiaryItemState[] =>
  state.notConnected.showConnectAllDialog
    ? state.notConnected.subsidiaries
    : state.portfolioResponse.subsidiaries
        .filter((s) =>
          state.notConnected.companies.some((c) => c.organisationNumber === s.organizationNumber),
        )
        .map<ConnectSubsidiaryItemState>((subsidiary) => ({
          checked: true,
          receiverEmail: new StringFieldModel({ required: true, validator: emailValidator }),
          subsidiary,
        }));

const updateNotConnectedTabState = (
  state: ReadyState,
  update: Partial<NotConnectedTabState>,
): DCEvent =>
  updateStore(state, {
    notConnected: {
      ...state.notConnected,
      ...update,
    },
  });

const submitConnectAll = (state: ReadyState, autoonboard: boolean): DCEvent[] => {
  state.notConnected.subsidiaries.forEach((s) => {
    s.receiverEmail.setRequired(s.checked && !autoonboard);
    s.receiverEmail.validate();
  });
  if (state.notConnected.subsidiaries.every((s) => s.receiverEmail.isValid)) {
    const data: ConnectCompaniesRequest = {
      autoonboard,
      portfolioId: state.portfolioResponse.portfolio?.id,
      connectData: state.notConnected.subsidiaries
        .filter((s) => s.checked)
        .map((s) => ({
          company: {
            companyName: s.subsidiary.name,
            organisationNumber: s.subsidiary.organizationNumber,
          },
          receiverEmail: s.receiverEmail.validatedValue,
        })),
    };
    const onSuccess = (): DCEvent[] => [
      updateNotConnectedTabState(state, {
        connectAllCompaniesResult: undefined,
        showConnectAllDialog: false,
      }),
      showAlert({ key: "general-success" }),
    ];

    const onFailure = (e: unknown): DCEvent[] => [
      updateNotConnectedTabState(state, {
        connectAllCompaniesResult: failure({ key: "general-error" }),
      }),
      updateNotConnectedTabState(state, {
        connectAllCompaniesResult: undefined,
        showConnectAllDialog: false,
      }),
      showError({
        key: isHttpError(HttpStatus.NOT_FOUND, e) ? "general-missing-access" : "general-error",
      }),
    ];

    return [
      updateNotConnectedTabState(state, {
        connectAllCompaniesResult: pending(),
      }),
      portfolioApi.connectAllCompanies({ data, onSuccess, onFailure }),
    ];
  } else {
    return [];
  }
};

export type ConnectCompaniesRequest = {
  connectData: ConnectData[];
  autoonboard: boolean;
  portfolioId?: string;
};

export type ConnectData = {
  company: CompanyItem;
  receiverEmail: string;
};

export type AcceptUserTermsAndUpdateEmailRequest = {
  email: string;
};

const addCompanyToPortfolioActions = (
  dispatch: (events: DCEvent[]) => void,
  portfolioState: ReadyState,
  store: PortfolioPageStore,
  setPortfolioFollowUpCount?: (count: number) => void,
): AddCompanyToPortfolioActions => ({
  openAddCompanyDialog: () => dispatch(openCompanyDialog(portfolioState)),
  closeAddCompanyDialog: () => dispatch(closeCompanyDialog(portfolioState)),
  toggleCompanyInAddDialog: (orgNo) => dispatch(toggleCompanyInAddDialog(portfolioState, orgNo)),
  isCompanySelected: (orgNo) => portfolioState.selectedCompanies.indexOf(orgNo) > -1,
  saveCompaniesInPortfolio: () =>
    dispatch(
      addCompaniesToPortfolio(
        portfolioState,
        initPortfolioCompaniesPage({
          dispatch,
          currentUrl: portfolioState.currentUrl,
          portfolio: portfolioState.portfolio,
          activeHeader: portfolioState.activeHeader,
          companiesTab: portfolioState.companiesTab,
          companiesMode: portfolioState.companiesMode,
          signDocumentCounts: portfolioState.signDocumentCounts,
          store,
          setPortfolioFollowUpCount,
          force: true,
        }),
      ),
    ),
  toggleAllCompanies: () => dispatch(toggleAllCompanies(portfolioState)),
  deselectAllCompanies: () => dispatch([updateStore(portfolioState, { selectedCompanies: [] })]),
});

const searchActions = (
  dispatch: (events: DCEvent[]) => void,
  portfolioState: ReadyState,
): CompanySearchActions => ({
  openNorwegianCompanySearch: () => dispatch(toggleNorwegianCompanySearch(portfolioState, true)),
  openSwedishCompanySearch: () => dispatch(toggleSwedishCompanySearch(portfolioState, true)),
  closeNorwegianCompanySearch: () => dispatch(toggleNorwegianCompanySearch(portfolioState, false)),
  closeSwedishCompanySearch: () => dispatch(toggleSwedishCompanySearch(portfolioState, false)),
  selectCompany: (company) => dispatch(selectCompanyFromSearchResult(portfolioState, company)),
  selectSwedishCompany: (company) => dispatch(selectSwedishCompany(portfolioState, company)),
  searchForCompany: (query) => dispatch(searchForCompany(portfolioState, query)),
  sendAskForPermissionRequest: (autoonboard: boolean) =>
    dispatch(sendAskForPermissionRequest(portfolioState, autoonboard)),
  searchSwedishBusinessRegister: () => dispatch(searchSwedishBusinessRegister(portfolioState)),
});

const newProjectActions = (
  dispatch: (events: DCEvent[]) => void,
  portfolioState: ReadyState,
  t: TFunction,
): NewProjectActions => ({
  openNewProjectDialog: () => dispatch(newProjectDialog(portfolioState)),
  closeNewProjectDialog: () => dispatch(closeProjectDialog(portfolioState)),
  toggleDecision: (decisionType) => dispatch(toggleDecision(portfolioState, decisionType)),
  checkedDecisions: portfolioState.checkedDecisions,
  companiesMode: portfolioState.companiesMode,
  createBatchProject: () =>
    dispatch(
      createBatchProject(
        portfolioState,
        createProjectName({ decisions: portfolioState.checkedDecisions, t }),
      ),
    ),
});

const portfolioInstanceActions = (
  portfolioState: ReadyState,
  dispatch: (events: DCEvent[]) => void,
  store: PortfolioPageStore,
  isSystemPortfolio: boolean,
  setCustomRoleId?: (roleId: string) => void,
  setPortfolioFollowUpCount?: (count: number) => void,
): PortfolioInstanceActions => ({
  isRenderStar: (orgNo: string, accessType: AccessType) =>
    isStarred(orgNo, accessType, portfolioState),
  onClickStar: (orgNo: string, accessType: AccessType) =>
    dispatch(toggleStarred(portfolioState, orgNo, accessType)),
  onAddAdministrator: ({ companyItem, lang }: { companyItem: CompanyItem; lang: Language }) =>
    dispatch(openSendAdministratorInvitationDialog(companyItem, lang)),
  onNewMeeting: (orgNo, meetingType) => dispatch([{ type: "new-meeting", orgNo, meetingType }]),
  onSelect: (company: PortfolioCompany) =>
    dispatch(selectCompany(portfolioState, dispatch, store, company, setCustomRoleId)),
  filterWithText: () => dispatch(filterPortfolioWithText(portfolioState)),
  onRemoveFromPortfolio: isSystemPortfolio
    ? undefined
    : (company: PortfolioCompany) =>
        dispatch(openRemoveFromPortfolioDialog(portfolioState, company)),
  closeRemoveCompanyDialog: () => dispatch(closeRemoveFromPortfolioDialog(portfolioState)),
  removeFromPortfolio: () => {
    dispatch(
      removeFromPortfolio(
        portfolioState,
        initPortfolioCompaniesPage({
          dispatch,
          currentUrl: portfolioState.currentUrl,
          portfolio: portfolioState.portfolio,
          activeHeader: portfolioState.activeHeader,
          companiesTab: portfolioState.companiesTab,
          companiesMode: portfolioState.companiesMode,
          signDocumentCounts: portfolioState.signDocumentCounts,
          store,
          setPortfolioFollowUpCount,
          force: true,
        }),
      ),
    );
  },
  onMoveToPortfolio: isSystemPortfolio
    ? undefined
    : (company: PortfolioCompany) => dispatch(openMoveToPortfolioDialog(portfolioState, company)),
  closeMoveCompanyToPortfolioDialog: () => dispatch(closeMoveToPortfolioDialog(portfolioState)),
  setDestinationPortfolio: (portfolio: Portfolio) =>
    dispatch(setDestinationPortfolio(portfolioState, portfolio)),
  moveCompanyToPortfolio: () => dispatch(moveCompanyToPortfolio(portfolioState)),
  openDBotProjectList: () => dispatch(openDBotProjectList(portfolioState)),
  openBeneficialOwnersPage: (company: PortfolioCompany) =>
    dispatch([navigate(adminBeneficialOwners(company.organisationNumber))]),
  openARPage: (company: PortfolioCompany) =>
    dispatch([navigate(adminShareholdersAltinn(company.organisationNumber))]),
  copyToClipboard: (text) => dispatch([{ type: "copy-to-clipboard", text }]),
});

const openDBotProjectList = (state: ReadyState): DCEvent[] => {
  const organisationNumber = state.userCompaniesInPortfolio
    .filter((c) => c.accessType === AccessType.ADMIN)
    .find((a) =>
      state.portfolioResponse.companyItems.filter(
        (s) => s.organisationNumber === a.organisationNumber,
      ),
    )!.organisationNumber;
  return [navigate(adminProjects(organisationNumber))];
};

export const filterStarredInPortfolio = (
  userCompaniesInPortfolio: PortfolioCompany[],
  starred?: StarredCompany[],
): PortfolioCompany[] =>
  userCompaniesInPortfolio.filter(
    (c) =>
      starred?.some(
        (s) => s.organisationNumber === c.organisationNumber && s.accessType === c.accessType,
      ) ?? false,
  );

export const filterNotConnected = (
  companies: PortfolioCompany[],
  roleFilter: RoleFilter,
): PortfolioCompany[] =>
  companies.filter((c) => {
    return (
      !c.isCustomer &&
      (roleFilter === "all" ||
        (roleFilter === "ceo" && c.isCEO) ||
        (roleFilter === "chairperson" && c.isChairperson) ||
        (roleFilter === "member" && c.isBoardmember) ||
        (roleFilter === "shareholder" && c.isShareholder))
    );
  });

export function createCompaniesTabs(
  response: PortfolioResponse,
  dispatch: (events: DCEvent[]) => void,
  allCompanies: PortfolioCompany[],
  companiesTab: PortfolioCompaniesTab,
  portfolio: SelectedPortfolioOption,
  notConnected: NotConnectedTabState,
): TabButtonProps[] {
  const isAdmin = hasAdminAccess(response);
  return compact([
    {
      id: "all",
      label: {
        key: "portfolio.all-companies-with-count",
        params: { count: allCompanies.filter((c) => c.isCustomer).length },
      },
      onSelect: () => dispatch([navigateToCompaniesTabAction("all", portfolio)]),
      isActive: companiesTab === "all",
    },
    {
      id: "starred",
      label: {
        key: "companyStatus.starred",
        params: {
          count: filterStarredInPortfolio(allCompanies, response.userSettings.starredCompanies)
            .length,
        },
      },
      onSelect: () => dispatch([navigateToCompaniesTabAction("starred", portfolio)]),
      isActive: companiesTab === "starred",
    },
    {
      id: "not-connected",
      label: {
        key: "companyStatus.not-connected",
        params: {
          count: notConnected.companies.length,
        },
      },
      onSelect: () => dispatch([navigateToCompaniesTabAction("not-connected", portfolio)]),
      isActive: companiesTab === "not-connected",
    },
    isAdmin && showArStatus(portfolio)
      ? {
          id: "ar-status",
          label: { key: "companyStatus.ar-status" },
          onSelect: () => dispatch([navigateToCompaniesTabAction("ar-status", portfolio)]),
          isActive: companiesTab === "ar-status",
        }
      : undefined,
    isAdmin && showBeneficialOwnersTab(portfolio)
      ? {
          id: "beneficial-owners",
          label: { key: "companyStatus.beneficial-owners" },
          onSelect: () => dispatch([navigateToCompaniesTabAction("beneficial-owners", portfolio)]),
          isActive: companiesTab === "beneficial-owners",
        }
      : undefined,
  ]);
}

export function hasAdminAccess(response: PortfolioResponse) {
  return response.userCompanies.some((c) => c.accessType === AccessType.ADMIN);
}

function showArStatus(portfolio: SelectedPortfolioOption) {
  return portfolio.type !== "with-board-role" && portfolio.type !== "my-investments";
}

function showBeneficialOwnersTab(portfolio: SelectedPortfolioOption) {
  return portfolio.type !== "with-board-role" && portfolio.type !== "my-investments";
}

export const isStarred = (orgNo: string, accessType: AccessType, state: ReadyState): boolean =>
  state.portfolioResponse.userSettings.starredCompanies?.some(
    (c) =>
      c.organisationNumber === orgNo &&
      c.accessType === accessType &&
      state.userCompaniesInPortfolio.some(
        (p) => p.organisationNumber === orgNo && p.accessType === accessType,
      ),
  ) ?? false;

export const toggleStarred = (
  state: ReadyState,
  orgNo: string,
  accessType: AccessType,
): DCEvent[] => {
  const isCurrentlyStarred = isStarred(orgNo, accessType, state);
  const httpRequest = isCurrentlyStarred ? portfolioApi.unstarCompany : portfolioApi.starCompany;
  const filteredCompanies = filterStarredInPortfolio(
    state.userCompaniesInPortfolio,
    state.portfolioResponse.userSettings.starredCompanies,
  );

  const starredCompanies = isCurrentlyStarred
    ? (filteredCompanies.filter(
        (c) => c.organisationNumber !== orgNo || c.accessType !== accessType,
      ) ?? [])
    : [...filteredCompanies, { organisationNumber: orgNo, accessType }];

  const userCompaniesInPortfolio =
    state.companiesTab === "starred"
      ? state.userCompaniesInPortfolio.filter((c) =>
          starredCompanies.some(
            (s) => s.organisationNumber === c.organisationNumber && s.accessType === c.accessType,
          ),
        )
      : state.userCompaniesInPortfolio;

  const onSuccess = (): DCEvent[] => [
    updateStore(state, {
      userCompaniesInPortfolio,
      portfolioResponse: {
        ...state.portfolioResponse,
        userSettings: {
          ...state.portfolioResponse.userSettings,
          starredCompanies,
        },
      },
      companiesTabs: state.companiesTabs.map((t) => ({
        ...t,
        label:
          t.id === "starred"
            ? { key: "companyStatus.starred", params: { count: starredCompanies.length } }
            : t.id === "not-connected"
              ? {
                  key: "companyStatus.not-connected",
                  params: { count: state.notConnected.companies.length },
                }
              : t.label,
      })),
    }),
  ];
  const onFailure = () => [];
  return [
    httpRequest({
      orgNo,
      accessType,
      onSuccess,
      onFailure,
    }),
  ];
};

export function toPortfolioCompanies(data: InvitationResponse[]): PortfolioCompany[] {
  const seenOrganisationNumbers = new Set<string>();

  return data.reduce<PortfolioCompany[]>((acc, it) => {
    if (!seenOrganisationNumbers.has(it.organisationNumber)) {
      seenOrganisationNumbers.add(it.organisationNumber);
      acc.push({
        id: it.id,
        organisationNumber: it.organisationNumber,
        name: it.companyName,
        isCustomer: false,
        accessType: AccessType.DRAFT,
        state: it.state as CompanyState,
        starred: false,
        currency: it.currency,
      });
    }
    return acc;
  }, []);
}

export const subscriptionStatus = ["all", "active", "inactive"] as const;
export type ActiveSubscribedCompanies = (typeof subscriptionStatus)[number];

export function activeCompanySubscriptionsLabel(status: ActiveSubscribedCompanies): TMessageKey {
  switch (status) {
    case "all":
      return "portfolio.companies-active-subscription-all";
    case "active":
      return "portfolio.companies-active-subscription-active";
    case "inactive":
      return "portfolio.companies-active-subscription-inactive";
  }
}

export const prepareCompaniesWithSubscriptionStatus = (
  value: ActiveSubscribedCompanies | undefined,
  onSelect: (value: ActiveSubscribedCompanies) => void,
): SelectProps<ActiveSubscribedCompanies> => ({
  value: value ?? "all",
  onSelect,
  items: subscriptionStatus.slice(),
  idFn: identity,
  labelFn: (option) => ({ key: activeCompanySubscriptionsLabel(option) }),
  iconFn: (option) => {
    switch (option) {
      case "active":
        return { name: "circle", color: "success", weight: "fill", size: "xs" };
      case "inactive":
        return { name: "circle", color: "subtle", size: "xs" };
      case "all":
        return "list-bullets";
    }
  },
});
